/**
 * PartnerFormPage — F-2. 시공반장님 등록 / 편집
 *
 * - /partners/new          → 등록 모드 (+ 반장님 더 추가 지원)
 * - /partners/:id/edit     → 편집 모드 (location.state.partner 또는 목록 조회 fallback)
 */
import { useState, useEffect, useMemo, type CSSProperties } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Badge, Button, Checkbox, Input, MultiSelect, Segmented, Select, KeywordForm } from '@indipia/ui-core';
import { SIGUNGU_LIST, COMM_METHOD_OPTIONS, COMM_METHOD_HELP_CONSTRUCTION } from '@indipia/shared';
import { fetchPartners, createPartner, updatePartner, checkPartnerExists, fetchMainPartnerWorkTypes } from '../api/partners';
import type { PartnerItem } from '../api/partners';
import { fetchWorkTypes, fetchCommonCodes } from '../api/work-types';

// 활동지역 시군구 옵션(전체) + 코드→명 조회 — @indipia/shared 정적 데이터
const SIGUNGU_OPTIONS = SIGUNGU_LIST.map((s) => ({ value: s.code, label: s.name }));
const SIGUNGU_NAME_BY_CODE = new Map(SIGUNGU_LIST.map((s) => [s.code, s.name]));

type OptionItem = { value: string; label: string };

// ── 타입 ──────────────────────────────────────────────────────────────────────

interface FormEntry {
  partnerName: string;
  phone: string;
  mainCode: string;        // 주작업공종 (단일)
  subCodes: string[];      // 부작업공종 (다중)
  partnerType: string;     // 파트너 유형 (공통코드 E01_PARTNER_TYPE: FOREMAN/COMPANY)
  dealStatus: string;      // 거래 상태 (공통코드 E01_PARTNER_DEAL_STATUS: ACTIVE/SUSPENDED)
  commYn: boolean;         // 연락 방식 (true=알림톡 발송-회신 · false=결과만 직접등록, 기본 true)
  sigunguCodes: string[];  // 활동지역 시군구 코드 (다중)
  tags: string[];          // 전문분야 태그
  memo: string;            // 한줄메모
  hasBiz: boolean;
  partnerBizNo: string;
  partnerBizName: string;
  partnerRepName: string;
}

const EMPTY_FORM: FormEntry = {
  partnerName: '',
  phone: '',
  mainCode: '',
  subCodes: [],
  partnerType: 'FOREMAN', // 파트너 유형 기본값 = 시공반장
  dealStatus: 'ACTIVE',   // 거래 상태 기본값 = 거래중
  commYn: true,           // 연락 방식 기본값 = 알림톡 발송-회신
  sigunguCodes: [],
  tags: [],
  memo: '',
  hasBiz: false,
  partnerBizNo: '',
  partnerBizName: '',
  partnerRepName: '',
};

// ── 헬퍼 ──────────────────────────────────────────────────────────────────────

function formatBizNo(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.startsWith('02')) {
    const d = digits.slice(0, 10);
    if (d.length <= 2) return d;
    if (d.length <= 5) return `${d.slice(0, 2)}-${d.slice(2)}`;
    if (d.length <= 9) return `${d.slice(0, 2)}-${d.slice(2, 5)}-${d.slice(5)}`;
    return `${d.slice(0, 2)}-${d.slice(2, 6)}-${d.slice(6)}`;
  }
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (digits.length <= 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}

function validate(form: FormEntry): string | null {
  if (!form.partnerName.trim()) return '이름을 입력해주세요.';
  if (!form.phone.trim()) return '연락처를 입력해주세요.';
  if (!form.partnerType) return '파트너 유형을 선택해주세요.';
  if (!form.dealStatus) return '거래 상태를 선택해주세요.';
  if (!form.mainCode) return '주작업공종을 선택해주세요.';
  if (form.sigunguCodes.length === 0) return '활동지역을 선택해주세요.';
  // 사업자 정보(번호·상호·대표)는 옵션 — 입력 안 해도 됨
  return null;
}

// forUpdate=true(수정): 서버 계약상 null=미변경·""=비움 → 비운 선택 입력은 빈 문자열로 전송. 등록은 기존대로 null.
function toPayload(form: FormEntry, forUpdate = false) {
  return {
    partnerName: form.partnerName.trim(),
    mobile: form.phone.trim(),
    specialtyTypes: form.mainCode || undefined,                 // 주작업공종(단일)
    partnerType: form.partnerType || null,                       // 파트너 유형
    dealStatus: form.dealStatus || null,                         // 거래 상태
    commYn: form.commYn ? 'Y' : 'N',                             // 연락 방식
    subWorkTypes: form.subCodes.filter((c) => c !== form.mainCode), // 부작업공종(주작업 제외 재확인)
    sigungus: form.sigunguCodes.map((c) => ({ code: c, name: SIGUNGU_NAME_BY_CODE.get(c) ?? c })),
    tags: form.tags,                                             // 전문분야 태그
    memo: forUpdate ? form.memo.trim() : (form.memo.trim() || null), // 한줄메모(수정 시 빈 문자열=비움)
    partnerBizYn: form.hasBiz ? 'Y' : 'N',
    partnerBizNo: form.hasBiz && form.partnerBizNo.trim() ? form.partnerBizNo.trim() : null,
    partnerBizName: form.hasBiz && form.partnerBizName.trim() ? form.partnerBizName.trim() : null,
    partnerRepName: form.hasBiz && form.partnerRepName.trim() ? form.partnerRepName.trim() : null,
  };
}

// 서버 에러 메시지(예: 중복) 추출 — 없으면 null
function serverMsg(e: unknown): string | null {
  const m = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
  return m || null;
}

const INPUT_CLS = 'placeholder:text-text-muted focus:border-group focus:ring-0';

// 라벨-입력 한 줄 정렬용 2열 그리드(라벨 고정폭 + 입력). 모든 행 동일 구조로 좌측 정렬 일치.
const ROW_GRID: CSSProperties = { display: 'grid', gridTemplateColumns: '6rem 1fr', alignItems: 'center', columnGap: '12px' };

// ── 서브 컴포넌트 ────────────────────────────────────────────────────────────

type BizNoCheckStatus = 'idle' | 'checking' | 'ok' | 'duplicate';

function FormCard({
  form,
  onChange,
  workTypes,
  partnerTypeOptions,
  dealStatusOptions,
  dupStatus,
  createdByName,
}: {
  form: FormEntry;
  onChange: (next: FormEntry) => void;
  workTypes: { workTypeCode: string; workTypeName: string }[];
  partnerTypeOptions: OptionItem[];
  dealStatusOptions: OptionItem[];
  dupStatus: BizNoCheckStatus;
  createdByName?: string;
}) {
  // 사업자번호가 10자리면 사업자번호로, 아니면 연락처로 중복확인 → 중복 표시 위치도 그에 맞춘다
  const bizNoComplete = form.partnerBizNo.replace(/\D/g, '').length === 10;
  const workTypeOptions: OptionItem[] = workTypes.map((w) => ({ value: w.workTypeCode, label: w.workTypeName }));
  // 부작업공종 옵션 — 주작업공종으로 선택된 항목은 목록에 보이되 비활성(선택불가)
  const subOptions = workTypeOptions.map((o) =>
    o.value === form.mainCode ? { ...o, label: `${o.label} (주작업)`, disabled: true } : o,
  );

  return (
    <div className="rounded-xl border border-border bg-surface-raised p-7">
      {/* ── 기본 정보 ── */}
      <div className="mb-3.5 flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-widest text-text-muted">기본 정보</p>
        {createdByName && (
          <span className="text-caption text-text-disabled">등록자 {createdByName}</span>
        )}
      </div>
      <div className="border-b border-border pb-2">
        {/* 파트너 유형 */}
        <div className="py-1.5" style={ROW_GRID}>
          <label className="text-xs font-semibold text-text-secondary">파트너 유형 <span className="text-error">*</span></label>
          <div>
            <Segmented
              value={form.partnerType}
              onChange={(v) => onChange({ ...form, partnerType: v })}
              options={partnerTypeOptions}
            />
          </div>
        </div>
        {/* 이름 */}
        <div className="py-1.5" style={ROW_GRID}>
          <label className="text-xs font-semibold text-text-secondary">이름 <span className="text-error">*</span></label>
          <Input
            value={form.partnerName}
            onChange={(e) => onChange({ ...form, partnerName: e.target.value })}
            placeholder="반장님 성함 (업체는 대표 이름)"
            className={INPUT_CLS}
          />
        </div>
        {/* 연락처 */}
        <div className="py-1.5" style={ROW_GRID}>
          <label className="text-xs font-semibold text-text-secondary">연락처 <span className="text-error">*</span></label>
          <div>
            <Input
              value={form.phone}
              onChange={(e) => onChange({ ...form, phone: formatPhone(e.target.value) })}
              placeholder="010-0000-0000"
              className={INPUT_CLS}
            />
            {/* 사업자번호(옵션)가 10자리면 그쪽으로 중복확인 → 여기선 연락처 기준일 때만 표시 */}
            {!bizNoComplete && dupStatus === 'checking' && <p className="mt-1.5 text-xs text-text-muted">중복 확인 중…</p>}
            {!bizNoComplete && dupStatus === 'ok' && <p className="mt-1.5 text-xs font-semibold text-success">✓ 등록 가능한 연락처예요</p>}
            {!bizNoComplete && dupStatus === 'duplicate' && <p className="mt-1.5 text-xs font-semibold text-error">이미 등록된 연락처예요</p>}
          </div>
        </div>
        {/* 거래 상태 */}
        <div className="py-1.5" style={ROW_GRID}>
          <label className="text-xs font-semibold text-text-secondary">거래 상태 <span className="text-error">*</span></label>
          <div>
            <Segmented
              value={form.dealStatus}
              onChange={(v) => onChange({ ...form, dealStatus: v })}
              options={dealStatusOptions}
            />
            <p className="mt-1 text-xs text-text-muted">거래중지면 공사스케줄에 시공파트너 배정 후보에서 빠져요</p>
          </div>
        </div>
        {/* 연락 방식 */}
        <div className="py-1.5" style={ROW_GRID}>
          <label className="text-xs font-semibold text-text-secondary">연락 방식 <span className="text-error">*</span></label>
          <div>
            <Segmented
              value={form.commYn ? 'Y' : 'N'}
              onChange={(v) => onChange({ ...form, commYn: v === 'Y' })}
              options={COMM_METHOD_OPTIONS}
            />
            <p className="mt-1 text-xs text-text-muted">{COMM_METHOD_HELP_CONSTRUCTION}</p>
          </div>
        </div>
      </div>

      {/* ── 작업 공종 ── */}
      <p className="mb-3.5 mt-5 text-xs font-bold uppercase tracking-widest text-text-muted">작업 공종</p>
      <div className="border-b border-border pb-2">
        <div className="py-1.5" style={ROW_GRID}>
          <label className="text-xs font-semibold text-text-secondary">주작업공종 <span className="text-error">*</span></label>
          <div>
            <Select
              value={form.mainCode}
              onChange={(e) => {
                const code = e.target.value;
                onChange({ ...form, mainCode: code, subCodes: form.subCodes.filter((c) => c !== code) });
              }}
            >
              <option value="">선택해주세요</option>
              {workTypes.map((w) => (
                <option key={w.workTypeCode} value={w.workTypeCode}>{w.workTypeName}</option>
              ))}
            </Select>
            <p className="mt-1 text-xs text-text-muted">자동배정 시 공사스케줄 공종과 주작업공종이 같은 반장님을 먼저 찾아요</p>
          </div>
        </div>
        <div className="py-1.5" style={ROW_GRID}>
          <label className="text-xs font-semibold text-text-secondary">부작업공종</label>
          <div>
            <MultiSelect
              values={form.subCodes}
              onChange={(v) => onChange({ ...form, subCodes: v })}
              options={subOptions}
              searchable
              placeholder="공종 선택 (여러 개)"
            />
            <p className="mt-1 text-xs text-text-muted">여러 개 · 참고용</p>
          </div>
        </div>
        <div className="py-2.5">
          <label className="mb-1.5 block text-xs font-semibold text-text-secondary">전문분야 태그</label>
          <KeywordForm
            tags={form.tags}
            onAdd={(v) => onChange({ ...form, tags: [...form.tags, v] })}
            onRemove={(i) => onChange({ ...form, tags: form.tags.filter((_, idx) => idx !== i) })}
            placeholder="예: 대형타일 입력 후 엔터"
            help="목록 카드에 함께 보여요"
          />
        </div>
      </div>

      {/* ── 배정 기준 ── */}
      <p className="mb-3.5 mt-5 text-xs font-bold uppercase tracking-widest text-text-muted">배정 기준</p>
      <div className="border-b border-border pb-2">
        <div className="py-2.5">
          <label className="mb-1.5 block text-xs font-semibold text-text-secondary">활동지역 <span className="text-error">*</span></label>
          <MultiSelect
            values={form.sigunguCodes}
            onChange={(v) => onChange({ ...form, sigunguCodes: v })}
            options={SIGUNGU_OPTIONS}
            searchable
            placeholder="시·구 검색 (예: 강남)"
          />
          <p className="mt-1 text-xs text-text-muted">공사스케줄에서 시공파트너 자동배정 시 활동지역을 체크해요</p>
        </div>
      </div>

      {/* ── 메모 ── */}
      <p className="mb-3.5 mt-5 text-xs font-bold uppercase tracking-widest text-text-muted">메모</p>
      <div>
        <div className="py-2.5">
          <label className="mb-1.5 block text-xs font-semibold text-text-secondary">한줄메모</label>
          <Input
            value={form.memo}
            onChange={(e) => onChange({ ...form, memo: e.target.value })}
            placeholder="예: 주말 작업 가능 (100자 이내)"
            width="fill"
            maxLength={100}
            className="!mb-0 placeholder:text-text-muted focus:border-group focus:ring-0"
          />
          <p className="mt-1 text-xs text-text-muted">목록 카드와 수동 배정 화면에 함께 보여요</p>
        </div>
        {/* 사업자 정보 — 옵션(체크 시 입력) */}
        <div className="py-2.5">
          <Checkbox
            label="사업자 있음 (번호·상호·대표명 추가 입력)"
            checked={form.hasBiz}
            onChange={(e) =>
              onChange(
                e.target.checked
                  ? { ...form, hasBiz: true }
                  : { ...form, hasBiz: false, partnerBizNo: '', partnerBizName: '', partnerRepName: '' },
              )
            }
          />
          {form.hasBiz && (
            <div className="mt-2 space-y-2 border-l border-border pl-3">
              <div className="py-1" style={ROW_GRID}>
                <label className="text-xs font-semibold text-text-secondary">사업자번호</label>
                <div>
                  <Input
                    value={form.partnerBizNo}
                    onChange={(e) => onChange({ ...form, partnerBizNo: formatBizNo(e.target.value) })}
                    placeholder="000-00-00000"
                    className={INPUT_CLS}
                  />
                  {bizNoComplete && dupStatus === 'checking' && <p className="mt-1.5 text-xs text-text-muted">중복 확인 중…</p>}
                  {bizNoComplete && dupStatus === 'ok' && <p className="mt-1.5 text-xs font-semibold text-success">✓ 등록 가능한 사업자번호예요</p>}
                  {bizNoComplete && dupStatus === 'duplicate' && <p className="mt-1.5 text-xs font-semibold text-error">이미 등록된 사업자예요</p>}
                </div>
              </div>
              <div className="py-1" style={ROW_GRID}>
                <label className="text-xs font-semibold text-text-secondary">상호명</label>
                <Input
                  value={form.partnerBizName}
                  onChange={(e) => onChange({ ...form, partnerBizName: e.target.value })}
                  placeholder="상호명 입력"
                  className={INPUT_CLS}
                />
              </div>
              <div className="py-1" style={ROW_GRID}>
                <label className="text-xs font-semibold text-text-secondary">대표자명</label>
                <Input
                  value={form.partnerRepName}
                  onChange={(e) => onChange({ ...form, partnerRepName: e.target.value })}
                  placeholder="대표자명 입력"
                  className={INPUT_CLS}
                />
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}

function AddMoreConfirmModal({
  partnerName,
  onConfirm,
  onCancel,
  isPending,
}: {
  partnerName: string;
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  return (
    <div
      className="fixed inset-0 z-modal flex items-center justify-center p-4"
      style={{ background: 'var(--overlay-scrim)' }}
      onClick={onCancel}
    >
      <div
        className="w-full max-w-sm rounded-xl bg-surface-raised p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="partnerSaveConfirmTitle"
      >
        <p id="partnerSaveConfirmTitle" className="text-sm font-bold text-text">현재 입력 내용을 저장하시겠습니까?</p>
        <p className="mt-1.5 text-xs text-text-muted">
          <strong>{partnerName}</strong> 반장님 정보를 저장하고 다음 반장님을 추가합니다.
        </p>
        <div className="mt-5 flex gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            className="flex-1"
          >
            취소
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={onConfirm}
            disabled={isPending}
            loading={isPending}
            className="flex-1"
          >
            {isPending ? '저장 중…' : '저장 후 추가'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// 거래정지 시 주시공파트너 지정 해제 확인 모달
function SuspendReleaseConfirmModal({
  workNames,
  onConfirm,
  onCancel,
  isPending,
}: {
  workNames: string[];
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  return (
    <div
      className="fixed inset-0 z-modal flex items-center justify-center p-4"
      style={{ background: 'var(--overlay-scrim)' }}
      onClick={onCancel}
    >
      <div
        className="w-full max-w-sm rounded-xl bg-surface-raised p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="partnerSuspendConfirmTitle"
      >
        <p id="partnerSuspendConfirmTitle" className="text-sm font-bold text-text">거래정지 전 확인해주세요</p>
        <p className="mt-1.5 text-xs text-text-muted">
          이 시공반장은 <strong>[{workNames.join('·')}]</strong> 공종의 주시공파트너로 등록되어 있어요.
          거래정지하면 해당 공종의 주시공파트너에서 해제됩니다. 계속하시겠습니까?
        </p>
        <div className="mt-5 flex gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isPending}
            className="flex-1"
          >
            취소
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={onConfirm}
            disabled={isPending}
            loading={isPending}
            className="flex-1"
          >
            {isPending ? '처리 중…' : '거래정지'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── 메인 페이지 ───────────────────────────────────────────────────────────────

export function PartnerFormPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { partnerId } = useParams<{ partnerId: string }>();
  const location = useLocation();

  const isEdit = !!partnerId;
  const { data: workTypes = [] } = useQuery({
    queryKey: ['work-types'],
    queryFn: fetchWorkTypes,
  });

  // 파트너 유형 옵션 — 공통코드 E01_PARTNER_TYPE (FOREMAN/COMPANY)
  const { data: partnerTypeCodes = [] } = useQuery({
    queryKey: ['common-codes', 'E01_PARTNER_TYPE'],
    queryFn: () => fetchCommonCodes('E01_PARTNER_TYPE'),
  });
  const partnerTypeOptions = useMemo(
    () => partnerTypeCodes.map((c) => ({ value: c.codeValue, label: c.codeName })),
    [partnerTypeCodes],
  );

  // 거래 상태 옵션 — 공통코드 E01_PARTNER_DEAL_STATUS (ACTIVE/SUSPENDED)
  const { data: dealStatusCodes = [] } = useQuery({
    queryKey: ['common-codes', 'E01_PARTNER_DEAL_STATUS'],
    queryFn: () => fetchCommonCodes('E01_PARTNER_DEAL_STATUS'),
  });
  const dealStatusOptions = useMemo(
    () => dealStatusCodes.map((c) => ({ value: c.codeValue, label: c.codeName })),
    [dealStatusCodes],
  );

  // 편집 모드: location.state 또는 목록에서 조회
  const statePartner = (location.state as { partner?: PartnerItem } | null)?.partner;
  const { data: partnerList } = useQuery({
    queryKey: ['partners'],
    queryFn: () => fetchPartners(),
    enabled: isEdit && !statePartner,
  });
  const editPartner = statePartner ?? partnerList?.find((p) => p.partnerId === Number(partnerId));

  // 편집 초기값
  function partnerToForm(p: PartnerItem): FormEntry {
    const knownCodes = new Set(workTypes.map((w) => w.workTypeCode));
    const nameToCode = new Map(workTypes.map((w) => [w.workTypeName, w.workTypeCode]));
    const resolve = (v: string) => (knownCodes.has(v) ? v : (nameToCode.get(v) ?? v));
    // 주작업공종은 단일 — 콤마 포함 구데이터면 첫 코드만 채택
    const first = (p.specialtyTypes ?? '').split(',').map((v) => v.trim()).filter(Boolean)[0];
    return {
      partnerName: p.partnerName,
      phone: p.mobile,
      mainCode: first ? resolve(first) : '',
      subCodes: (p.subWorkTypes ?? []).map(resolve),
      partnerType: p.partnerType ?? '',       // 기존 NULL(미지정)은 빈값 유지 — 사용자가 직접 선택
      dealStatus: p.dealStatus ?? 'ACTIVE',   // 거래 상태 기본 거래중
      commYn: p.commYn !== 'N',               // 연락 방식 (미제공=알림톡 발송-회신)
      sigunguCodes: (p.sigungus ?? []).map((s) => s.code),
      tags: p.tags ?? [],
      memo: p.memo ?? '',
      hasBiz: p.partnerBizYn === 'Y',
      partnerBizNo: p.partnerBizNo ?? '',
      partnerBizName: p.partnerBizName ?? '',
      partnerRepName: p.partnerRepName ?? '',
    };
  }

  // 등록 모드 상태
  const [form, setForm] = useState<FormEntry>(() =>
    isEdit && editPartner ? partnerToForm(editPartner) : EMPTY_FORM,
  );
  const [savedSummaries, setSavedSummaries] = useState<{ partnerName: string; phone: string }[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [showAddConfirm, setShowAddConfirm] = useState(false);
  // 거래정지 저장 시 주시공파트너 지정 해제 확인
  const [showSuspendConfirm, setShowSuspendConfirm] = useState(false);
  const [suspendWorkCodes, setSuspendWorkCodes] = useState<string[]>([]);
  const [suspendChecking, setSuspendChecking] = useState(false);
  // 입력 즉시 중복 확인(디바운스) — 사업자번호가 있으면 사업자번호로, 없으면(개인이거나 미입력) 연락처로
  const [dupStatus, setDupStatus] = useState<BizNoCheckStatus>('idle');
  useEffect(() => {
    const useBizNo = form.hasBiz && form.partnerBizNo.replace(/\D/g, '').length === 10;
    const raw = useBizNo ? form.partnerBizNo : form.phone;
    const digits = raw.replace(/\D/g, '');
    if (digits.length < 10) { setDupStatus('idle'); return; }
    setDupStatus('checking');
    const t = setTimeout(() => {
      checkPartnerExists(
        useBizNo ? { partnerBizNo: form.partnerBizNo } : { mobile: form.phone },
        isEdit ? Number(partnerId) : undefined,
      )
        .then((exists) => setDupStatus(exists ? 'duplicate' : 'ok'))
        .catch(() => setDupStatus('idle'));
    }, 400);
    return () => clearTimeout(t);
  }, [form.hasBiz, form.phone, form.partnerBizNo, isEdit, partnerId]);

  // 편집 모드: 데이터 로드 후 form 동기화 (초기 statePartner 없을 때)
  const [formInitialized, setFormInitialized] = useState(!!statePartner || !isEdit);
  if (isEdit && editPartner && !formInitialized) {
    setForm(partnerToForm(editPartner));
    setFormInitialized(true);
  }

  const saveMutation = useMutation({
    mutationFn: async (vars: { payload: ReturnType<typeof toPayload>; release?: boolean }): Promise<void> => {
      if (isEdit) {
        await updatePartner(Number(partnerId), { action: 'update', ...vars.payload });
      } else {
        await createPartner(vars.payload);
      }
      // 주시공파트너 지정 해제는 서버(sv-member → sv-schedule)가 거래중지 저장 커밋 후 처리한다.
    },
    onSuccess: (_, vars) => {
      // 거래정지 확인 모달을 거쳐 저장한 경우에만 안내 문구 표시(API 호출 없음)
      if (vars.release) toast.success('주시공파트너 지정을 해제했습니다');
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      navigate('/partners');
    },
    onError: (e) => {
      setShowSuspendConfirm(false);
      setFormError(serverMsg(e) ?? '저장에 실패했습니다. 다시 시도해주세요.');
    },
  });

  const addMoreMutation = useMutation({
    mutationFn: (payload: ReturnType<typeof toPayload>) => createPartner(payload),
    onSuccess: (_, payload) => {
      setSavedSummaries((prev) => [...prev, { partnerName: payload.partnerName, phone: payload.mobile }]);
      setForm(EMPTY_FORM);
      setFormError(null);
      setShowAddConfirm(false);
      queryClient.invalidateQueries({ queryKey: ['partners'] });
    },
    onError: (e) => {
      setFormError(serverMsg(e) ?? '저장에 실패했습니다. 다시 시도해주세요.');
      setShowAddConfirm(false);
    },
  });

  // 중복이면 저장 불가 — 사업자번호(10자리) 있으면 사업자번호, 없으면 연락처 기준
  const dupBlocking = dupStatus === 'duplicate';
  const dupMessage = (form.hasBiz && form.partnerBizNo.replace(/\D/g, '').length === 10)
    ? '이미 등록된 사업자번호입니다.' : '이미 등록된 연락처입니다.';

  async function handleSave() {
    const err = validate(form);
    if (err) { setFormError(err); return; }
    if (dupBlocking) { setFormError(dupMessage); return; }
    setFormError(null);
    // 편집 + 거래정지로 저장 시: 주시공파트너 지정 여부 확인 후, 지정돼 있으면 확인 모달
    if (isEdit && form.dealStatus === 'SUSPENDED') {
      setSuspendChecking(true);
      try {
        const codes = await fetchMainPartnerWorkTypes(Number(partnerId));
        if (codes.length > 0) {
          setSuspendWorkCodes(codes);
          setShowSuspendConfirm(true);
          return;
        }
      } catch {
        // 조회 실패 시 지정 해제 없이 일반 저장으로 진행
      } finally {
        setSuspendChecking(false);
      }
    }
    saveMutation.mutate({ payload: toPayload(form, isEdit) });
  }

  function handleAddMore() {
    const err = validate(form);
    if (err) { setFormError(err); return; }
    if (dupBlocking) { setFormError(dupMessage); return; }
    setFormError(null);
    setShowAddConfirm(true);
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4 p-8">

      {/* 페이지 타이틀 */}
      <div>
        <h1 className="text-lg font-extrabold tracking-tight text-text">
          {isEdit ? '시공파트너 수정' : '시공파트너 등록'}
        </h1>
        <p className="mt-1 text-xs text-text-muted">
          {isEdit
            ? '반장님 정보를 수정합니다'
            : '함께 일하는 시공반장님 정보를 등록해두면, 공사 배정이 쉬워져요'}
        </p>
      </div>

      {/* 이미 저장된 반장님 요약 (등록 모드 + 더 추가 사용 시) */}
      {savedSummaries.length > 0 && (
        <div className="space-y-2">
          {savedSummaries.map((s, i) => (
            <div
              key={i}
              className="flex items-center gap-3 rounded-xl border border-border bg-surface-raised px-4 py-3"
            >
              <span
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-extrabold text-group"
                style={{ background: 'color-mix(in srgb, var(--gt-primary) 12%, transparent)' }}
              >
                {s.partnerName.charAt(0)}
              </span>
              <div className="min-w-0 flex-1">
                <span className="text-sm font-semibold text-text">{s.partnerName}</span>
                <span className="ml-2 text-xs text-text-muted">{s.phone}</span>
              </div>
              <Badge status="success">저장됨</Badge>
            </div>
          ))}
        </div>
      )}

      {/* 폼 카드 */}
      <FormCard
        form={form}
        onChange={setForm}
        workTypes={workTypes}
        partnerTypeOptions={partnerTypeOptions}
        dealStatusOptions={dealStatusOptions}
        dupStatus={dupStatus}
        createdByName={editPartner?.createdByName}
      />

      {/* 에러 메시지 */}
      {formError && (
        <p className="text-xs font-semibold text-error">{formError}</p>
      )}

      {/* 액션 버튼 */}
      <div className="flex gap-2.5">
        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate('/partners')}
          disabled={saveMutation.isPending || addMoreMutation.isPending}
          className="shrink-0"
        >
          취소
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={handleSave}
          disabled={saveMutation.isPending || suspendChecking || dupBlocking}
          loading={saveMutation.isPending || suspendChecking}
          className="flex-1"
        >
          {saveMutation.isPending ? '저장 중…' : '저장'}
        </Button>
        {!isEdit && (
          <Button
            type="button"
            variant="secondary"
            onClick={handleAddMore}
            disabled={addMoreMutation.isPending || dupBlocking}
            className="flex-1 !border-group !text-group hover:!bg-group hover:!text-group-onPrimary"
          >
            + 반장님 더 추가
          </Button>
        )}
      </div>

      {/* 더 추가 확인 모달 */}
      {showAddConfirm && (
        <AddMoreConfirmModal
          partnerName={form.partnerName}
          onConfirm={() => addMoreMutation.mutate(toPayload(form))}
          onCancel={() => setShowAddConfirm(false)}
          isPending={addMoreMutation.isPending}
        />
      )}

      {/* 거래정지 — 주시공파트너 지정 해제 확인 모달 */}
      {showSuspendConfirm && (
        <SuspendReleaseConfirmModal
          workNames={suspendWorkCodes.map(
            (c) => workTypes.find((w) => w.workTypeCode === c)?.workTypeName ?? c,
          )}
          onConfirm={() => saveMutation.mutate({ payload: toPayload(form, isEdit), release: true })}
          onCancel={() => setShowSuspendConfirm(false)}
          isPending={saveMutation.isPending}
        />
      )}
    </div>
  );
}
